import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUnread, markUnreadRead } from '../services/api';

const REFRESH_MS = 60000;
const EMPTY = { counts: {}, ids: {} };

const UnreadContext = createContext({
  counts: {},
  isUnread: () => false,
  markRead: () => {},
  refresh: () => {},
});

export const UnreadProvider = ({ children }) => {
  const { can } = useAuth();
  // Mêmes règles que le serveur (UNREAD_PERMISSIONS) : sans aucune, pas de requête.
  const enabled = Boolean(can) && (
    can('quotes.view') || can('cleaning_requests.view') || can('part_orders.view')
    || can('service_requests.view', 'all') || can('leave_requests.approve')
  );
  const [summary, setSummary] = useState(EMPTY);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await getUnread();
      setSummary(response.data || EMPTY);
    } catch (error) {
      // Les pastilles sont un confort : une erreur ne doit pas bloquer le portail.
      console.error('Error fetching unread counts:', error);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [enabled, refresh]);

  const isUnread = useCallback(
    (kind, id) => (summary.ids[kind] || []).includes(id),
    [summary]
  );

  const markRead = useCallback(async (kind, ids) => {
    const pending = (Array.isArray(ids) ? ids : [ids]).filter(id => isUnread(kind, id));
    if (!enabled || pending.length === 0) return;
    setSummary(current => {
      const remaining = (current.ids[kind] || []).filter(id => !pending.includes(id));
      return {
        ids: { ...current.ids, [kind]: remaining },
        counts: { ...current.counts, [kind]: remaining.length },
      };
    });
    try {
      await markUnreadRead(kind, pending);
    } catch (error) {
      console.error('Error marking as read:', error);
      refresh();
    }
  }, [enabled, isUnread, refresh]);

  const value = useMemo(
    () => ({ counts: summary.counts, isUnread, markRead, refresh }),
    [summary.counts, isUnread, markRead, refresh]
  );

  return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>;
};

export const useUnread = () => useContext(UnreadContext);

export const unreadTotal = (counts, kinds) =>
  kinds.reduce((total, kind) => total + (counts[kind] || 0), 0);
